import { inject } from "@angular/core";
import { signalStore, withState, withMethods, patchState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, switchMap, tap } from "rxjs";
import { PacksService } from "../infra/packs.service";
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { ErrorStore } from "@application/errors/data-access";
import { DxOdataStoreService } from "@shared/components/dx-data-grid/data-access";
import { DataSource } from "devextreme/common/data";
import { APP_CONFIG } from "@application/config";
import { TPack } from "../entities/packs.model";
import { tapResponse } from "@ngrx/operators";
import { HttpErrorResponse } from '@angular/common/http';
export interface IPacks {
  showPackModal: boolean;
  showPackGalleryModal: boolean;
  pack: TPack;
  packs: TPack[];
  packsTotal: number;
}

export const PacksInitialState: IPacks = {
  showPackModal: false,
  showPackGalleryModal: false,
  pack: null,
  packs: [],
  packsTotal: 0,
};

export const PacksStore = signalStore(
  { providedIn: 'root' },
  withState(PacksInitialState),
  withMethods((
    store,
    url = inject(APP_CONFIG).apiUrl,
    dxOdataStoreService = inject(DxOdataStoreService),
    packsService = inject(PacksService),
    errorStore = inject(ErrorStore)) => {

    return {

      togglePackModal: (show: boolean): void => {
        patchState(store, { showPackModal: show });
      },

      togglePackGalleryModal: (show: boolean): void => {
        patchState(store, { showPackGalleryModal: show });
      },

      getPacksDataSource: (): DataSource => {
        return dxOdataStoreService.fromOdata({
          url: url.odata_subscription_service,
          metadata: 'Packs',
          key: 'PackId',
          params: {
             $expand: 'Resources($expand=ResourcesRanges)'
          }
        });
      },

      getPack: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('getPack'))),
        switchMap((id: string) =>
          packsService.getPack(id).pipe(
            tapResponse({
              next: (pack) => {
                patchState(store, { pack, ...setPropLoaded('getPack')})
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getPack'));
              }
            })
          )
        )
      )),

      createPack: rxMethod<TPack>(pipe(
        tap(() => patchState(store, setPropLoading('createPack'))),
        switchMap((payload) => packsService.createPack(payload).pipe(
          tapResponse({
              next: () => {
                  patchState(store, { ...setPropLoaded('createPack') })
              },
              error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createPack'));
              }
          })
        ))
      )),

      updatePack: rxMethod<TPack>(pipe(
        tap(() => patchState(store, setPropLoading('updatePack'))),
        switchMap((payload) => packsService.updatePack(payload).pipe(
          tapResponse({
              next: () => {
                  patchState(store, { ...setPropLoaded('updatePack') })
              },
              error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('updatePack'));
              }
          })
        ))
      )),

      deletePack: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('deletePack'))),
        switchMap((payload) => packsService.deletePack(payload).pipe(
          tapResponse({
              next: () => {
                  patchState(store, { ...setPropLoaded('deletePack') })
              },
              error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('deletePack'));
              }
          })
        ))
      )),

      getPacks: rxMethod<{page: number, query?: string}>(pipe(
        tap(() => patchState(store, setPropLoading('getPacks'))),
        switchMap(({page, query}) =>
          packsService.getPacks(page, query).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { 
                  packs: response.value, 
                  packsTotal: response['@odata.count'] ?? 0,
                  ...setPropLoaded('getPacks') 
                });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getPacks'));
              }
            })
          )
        )
      )),

      resetReqState: () => {
        patchState(store, PacksInitialState, setPropInit('createPack', 'updatePack', 'deletePack', 'getPacks'));
      }
    }
  }),
  withReqState('createPack', 'updatePack', 'getPack', 'deletePack', 'getPacks'),
);
